import type { I_Brand } from '@enable-tech/shared-types/modules/brand/backend/responses/schemas';
import { E_LoyaltyCardStatus } from '@enable-tech/shared-types/modules/loyaltyProgram/common/backend/enums';
import type { ComponentChildren } from 'preact';
import { createContext } from 'preact';
import { useContext, useState } from 'preact/hooks';
import { isDesktop } from 'react-device-detect';

import { isAppleDevice } from '@/helpers/functions/deviceDetection';
import { isWalletAppConfigurationMissing, isWalletAppEnabled } from '@/helpers/functions/wallet';
import type { T_LoyaltyProgramContextType } from '@/types/Wallet';
import { E_WalletApp } from '@/types/Wallet';

export const LoyaltyProgramContext = createContext<T_LoyaltyProgramContextType | undefined>(
  undefined,
);

const LoyaltyProgramContextProvider = ({ children }: { children: ComponentChildren }) => {
  const [brandData, setBrandData] = useState<I_Brand | undefined>(undefined);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | undefined>(undefined);
  const [loyaltyCardStatus, setLoyaltyCardStatus] = useState<E_LoyaltyCardStatus | undefined>(
    undefined,
  );
  const [appliedCodes, setAppliedCodes] = useState<string[]>([]);
  const [inapplicableCodes, setInapplicableCodes] = useState<string[]>([]);
  // Codes limited to a Shopify collection: Shopify reports them as not applicable
  // until the cart holds a product from that collection, so they stay on the cart.
  const [collectionScopedCodes, setCollectionScopedCodes] = useState<string[]>([]);

  const isApple = Boolean(isDesktop || isAppleDevice());
  const isAndroid = isDesktop || !isAppleDevice();

  const enableWalletAvailable =
    isAndroid && isWalletAppEnabled(brandData, E_WalletApp.ENABLE_WALLET);

  const googleWalletAvailable =
    isAndroid &&
    (isWalletAppEnabled(brandData, E_WalletApp.GOOGLE_WALLET) ||
      isWalletAppConfigurationMissing(brandData));

  const walletPassesAvailable =
    isAndroid &&
    (isWalletAppEnabled(brandData, E_WalletApp.WALLET_PASSES) ||
      isWalletAppConfigurationMissing(brandData));

  const customerHasAddedLoyaltyCard = (loyaltyCardStatus: E_LoyaltyCardStatus) =>
    loyaltyCardStatus
      ? [
          E_LoyaltyCardStatus?.APPLE_WALLET,
          E_LoyaltyCardStatus?.WALLET_PASSES,
          E_LoyaltyCardStatus?.GOOGLE_WALLET,
          E_LoyaltyCardStatus.ENABLE_WALLET,
        ]?.includes(loyaltyCardStatus)
      : false;

  return (
    <LoyaltyProgramContext.Provider
      value={{
        isApple,
        isAndroid,
        enableWalletAvailable,
        googleWalletAvailable,
        walletPassesAvailable,
        brandName: brandData?.name ?? '',
        brandData,
        setBrandData,
        qrCodeUrl,
        setQrCodeUrl,
        loyaltyCardStatus,
        setLoyaltyCardStatus,
        customerHasAddedLoyaltyCard,
        appliedCodes,
        setAppliedCodes,
        inapplicableCodes,
        setInapplicableCodes,
        collectionScopedCodes,
        setCollectionScopedCodes,
      }}
    >
      {children}
    </LoyaltyProgramContext.Provider>
  );
};

export default LoyaltyProgramContextProvider;

export const useLoyaltyProgramContext = () => {
  const context = useContext(LoyaltyProgramContext);
  if (!context) {
    throw new Error('useLoyaltyProgramContext must be used within LoyaltyProgramContextProvider');
  }
  return context;
};
